export type BlockScheduleRow = {
  start: string;
  end: string;
};

export type BlockScheduleValidationResult = {
  errors: string[];
  warnings: string[];
  sortedRanges: string;
  sortedRows: BlockScheduleRow[];
};

export function validateBlockSchedule(input: string): BlockScheduleValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const ranges: { start: number; end: number; original: string }[] = [];

  // Allow empty input (zero ranges)
  if (input.trim() === "") {
    return { errors, warnings, sortedRanges: "", sortedRows: [] };
  }

  // Split input by commas.
  const parts = input.split(",");
  if (parts.length > 24) {
    errors.push("Too many ranges provided. Maximum allowed is 24.");
  }

  // Parse each range.
  for (const rawPart of parts) {
    const part = rawPart.trim();
    // Expecting a format like "5-12"
    const match = part.match(/^(\d+)-(\d+)$/);
    if (!match) {
      errors.push(`Invalid format for range: "${part}". Expected format "start-end".`);
      continue;
    }

    const start = parseInt(match[1], 10);
    const end = parseInt(match[2], 10);

    // Validate hours are between 0 and 23.
    if (start < 0 || start > 23 || end < 0 || end > 23) {
      errors.push(`Hour out of range in: "${part}". Hours must be between 0 and 23.`);
    }

    // Validate that the start is less than the end.
    if (start >= end) {
      errors.push(`Invalid range: "${part}". Start hour must be less than end hour.`);
    }

    ranges.push({ start, end, original: part });
  }

  // If there were any parsing/format errors, return immediately.
  if (errors.length > 0) {
    return { errors, warnings, sortedRanges: "", sortedRows: [] };
  }

  // Sort ranges by start time.
  ranges.sort((a, b) => a.start - b.start);

  // Check for overlaps and gaps.
  for (let i = 1; i < ranges.length; i++) {
    const prev = ranges[i - 1];
    const current = ranges[i];

    // Overlap: previous range's end is greater than current range's start.
    if (prev.end > current.start) {
      errors.push(`Ranges overlap: "${prev.original}" overlaps with "${current.original}".`);
    }
    // Gap: previous range's end is less than current range's start.
    if (prev.end < current.start) {
      warnings.push(`There is a gap between ${prev.original} and ${current.original}. Visits that fall into this gap will use arrival window offsets instead of block scheduling.`);
    }
  }

  const sortedRangesStr = ranges.map(r => r.original).join(",");
  const sortedRows = ranges.map(r => {return {start: r.start.toString(), end: r.end.toString()}})

  return { errors, warnings, sortedRanges: sortedRangesStr, sortedRows };
}

export function blockScheduleStringToRows(schedule: string): BlockScheduleRow[] {
  if (!schedule.trim()) return [];
  return schedule.split(",").map(rangeStr => {
    const [start, end] = rangeStr.split("-");
    return { start: start.trim(), end: end.trim() };
  });
}

export function blockScheduleRowsToString(rows: BlockScheduleRow[]): string {
  return rows.map(row => `${row.start}-${row.end}`).join(",");
}
